import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import { storage } from '../utils/storage';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// Función para obtener el restaurantId seleccionado dinámicamente
const getRestaurantId = () => {
  const restaurantId = storage.get('selectedRestaurantId');
  if (restaurantId) {
    return restaurantId;
  }

  // Fallback para desarrollo (remover en producción)
  console.warn('No se encontró selectedRestaurantId. Usuario debe seleccionar restaurante.');
  return null;
};

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Interceptor para agregar el token de autenticación a todas las peticiones
api.interceptors.request.use(
  async (config) => {
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();

      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      // Agregar headers de multi-tenancy si están disponibles
      const selectedOrgId = storage.get('selectedOrganizationId');
      const selectedRestaurantId = storage.get('selectedRestaurantId');

      if (selectedOrgId) {
        config.headers['X-Organization-Id'] = selectedOrgId;
      }

      if (selectedRestaurantId) {
        config.headers['X-Restaurant-Id'] = selectedRestaurantId;
      }
    } catch (error) {
      console.error('Error obteniendo token:', error);
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Multi-tenancy: Obtener organizaciones del usuario
export const getMyOrganization = async () => {
  const response = await api.get('/organizations/me');
  return response.data;
};

// Multi-tenancy: Obtener restaurantes del usuario en una organización
export const listMyRestaurants = async (organizationId) => {
  const response = await api.get(`/organizations/${organizationId}/my-restaurants`);
  return response.data;
};

// Listar restaurantes de la organización del usuario (legacy)
export const listRestaurants = async () => {
  const response = await api.get('/restaurants');
  return response.data;
};

// Obtener datos de un restaurante
export const getRestaurant = async () => {
  const restaurantId = getRestaurantId();
  if (!restaurantId) {
    throw new Error('No hay restaurante seleccionado');
  }

  const response = await api.get(`/restaurants/${restaurantId}`);
  return response.data;
};

// Obtener pedidos de un restaurante
export const getOrders = async (status = null, date = null, options = {}) => {
  const restaurantId = getRestaurantId();
  if (!restaurantId) {
    throw new Error('No hay restaurante seleccionado');
  }

  const params = new URLSearchParams();
  if (status) params.append('status', status);
  if (date) params.append('date', date);
  if (options.showAllAreas) params.append('showAllAreas', 'true');
  if (options.area) params.append('area', options.area);

  const response = await api.get(
    `/orders/restaurants/${restaurantId}?${params.toString()}`
  );
  return response.data;
};

// Actualizar estado de un pedido
export const updateOrderStatus = async (orderId, status) => {
  const restaurantId = getRestaurantId();
  if (!restaurantId) {
    throw new Error('No hay restaurante seleccionado');
  }

  const today = new Date().toISOString().split('T')[0];

  const response = await api.patch(`/orders/${orderId}/status`, {
    status,
    restaurantId,
    date: today
  });
  return response.data;
};

/**
 * Lista los repartidores propios del restaurante (own delivery).
 * @returns {Promise<{riders: Array}>}
 */
export const getRiders = async () => {
  const restaurantId = getRestaurantId();
  if (!restaurantId) {
    throw new Error('No hay restaurante seleccionado');
  }
  const response = await api.get(`/restaurants/${restaurantId}/riders`);
  return response.data;
};

/**
 * Asigna un repartidor propio a un pedido de delivery.
 * @param {string} orderId - ID del pedido
 * @param {string} riderId - ID del repartidor
 * @returns {Promise<Object>} Pedido con deliveryRider asignado
 */
export const assignRiderToOrder = async (orderId, riderId) => {
  const restaurantId = getRestaurantId();
  if (!restaurantId) {
    throw new Error('No hay restaurante seleccionado');
  }
  const today = new Date().toISOString().split('T')[0];
  const response = await api.post(`/orders/${orderId}/assign-rider`, {
    restaurantId,
    riderId,
    date: today
  });
  return response.data;
};

/**
 * Actualiza un pedido (campos generales como paymentStatus, paymentMethod)
 * @param {string} orderId - ID del pedido
 * @param {Object} updates - Campos a actualizar
 * @returns {Promise<Object>} Pedido actualizado
 */
export const updateOrder = async (orderId, updates) => {
  const restaurantId = getRestaurantId();
  if (!restaurantId) {
    throw new Error('No hay restaurante seleccionado');
  }

  const response = await api.put(`/orders/${orderId}?restaurantId=${restaurantId}`, updates);
  return response.data;
};

// Obtener mesas de un restaurante
export const getTables = async () => {
  const restaurantId = getRestaurantId();
  if (!restaurantId) {
    throw new Error('No hay restaurante seleccionado');
  }

  const response = await api.get(`/tables/restaurants/${restaurantId}`);
  return response.data;
};

// Obtener grupos de mesas
export const getGroups = async () => {
  const restaurantId = getRestaurantId();
  if (!restaurantId) {
    throw new Error('No hay restaurante seleccionado');
  }

  const response = await api.get(`/tables/restaurants/${restaurantId}/groups`);
  return response.data;
};

// Obtener pedidos de una sesión
export const getOrdersBySession = async (sessionId) => {
  const response = await api.get(`/orders/sessions/${sessionId}`);
  return response.data;
};

// Cerrar sesión de mesa
export const closeSession = async (sessionId, forceClose = false) => {
  const restaurantId = getRestaurantId();
  if (!restaurantId) {
    throw new Error('No hay restaurante seleccionado');
  }

  const response = await api.post(`/sessions/${sessionId}/close`, {
    restaurantId,
    forceClose
  });
  return response.data;
};

// Facturación AFIP
export const createInvoice = async (data) => {
  const response = await api.post('/invoicing/invoices', data);
  return response.data;
};

export const getFiscalConfig = async () => {
  const restaurantId = getRestaurantId();
  if (!restaurantId) {
    throw new Error('No hay restaurante seleccionado');
  }
  const response = await api.get(`/invoicing/restaurants/${restaurantId}/fiscal-config`);
  return response.data;
};

// Menú público de un restaurante
export const getMenu = async (restaurantId) => {
  const response = await api.get(`/menu/restaurants/${restaurantId}`);
  return response.data;
};

// Crear pedido
export const createOrder = async (orderData) => {
  const response = await api.post('/orders', orderData);
  return response.data;
};

// Iniciar sesión de mesa
export const startSession = async (restaurantId, tableNumber) => {
  const response = await api.post(
    `/tables/restaurants/${restaurantId}/${encodeURIComponent(tableNumber)}/session`
  );
  return response.data;
};

export default api;
